/**
 * Shared opportunity type color mapping
 * Used by OpportunityCard, OpportunityMap, and detail views for consistent coloring
 *
 * Colors are grouped by category. All sub-types within a category share one color
 * to avoid visual overwhelm. All colors meet WCAG AA contrast (4.5:1+) against white text.
 */

// 8 category colors: accessible against white text
const CATEGORY_COLORS = {
    workBasedLearning: '#1a73e8', // Blue: 4.6:1
    mentoring: '#7B1FA2', // Purple: 7.2:1
    programsCamps: '#D84315', // Deep orange: 5.3:1
    workshops: '#2E7D32', // Green: 5.1:1
    events: '#C62828', // Red: 6.3:1
    competitions: '#E65100', // Amber: 4.6:1
    funding: '#8B6914', // Gold-brown: 5.0:1
    community: '#00695C', // Teal: 5.5:1
};

// Map every opportunity type to its category color
const OPPORTUNITY_TYPE_COLORS = {
    // Work-Based Learning
    Internship: CATEGORY_COLORS.workBasedLearning,
    'Job Shadow': CATEGORY_COLORS.workBasedLearning,
    Apprenticeship: CATEGORY_COLORS.workBasedLearning,
    'Co-op Program': CATEGORY_COLORS.workBasedLearning,
    'Job/Employment': CATEGORY_COLORS.workBasedLearning,

    // Mentoring
    Mentorship: CATEGORY_COLORS.mentoring,
    'Coaching Program': CATEGORY_COLORS.mentoring,
    'Peer Mentoring': CATEGORY_COLORS.mentoring,

    // Programs & Camps
    'Summer Program': CATEGORY_COLORS.programsCamps,
    'After-School Program': CATEGORY_COLORS.programsCamps,
    Camp: CATEGORY_COLORS.programsCamps,
    Bootcamp: CATEGORY_COLORS.programsCamps,
    'Cohort Program': CATEGORY_COLORS.programsCamps,

    // Workshops & Training
    Workshop: CATEGORY_COLORS.workshops,
    'Training Session': CATEGORY_COLORS.workshops,
    'Certification Course': CATEGORY_COLORS.workshops,
    'Professional Development': CATEGORY_COLORS.workshops,
    Webinar: CATEGORY_COLORS.workshops,
    Seminar: CATEGORY_COLORS.workshops,

    // Events & Networking
    'Networking Event': CATEGORY_COLORS.events,
    Conference: CATEGORY_COLORS.events,
    'Career Fair': CATEGORY_COLORS.events,
    'Industry Tour': CATEGORY_COLORS.events,
    'Open House': CATEGORY_COLORS.events,

    // Competitions
    Competition: CATEGORY_COLORS.competitions,
    Hackathon: CATEGORY_COLORS.competitions,
    'Science Fair': CATEGORY_COLORS.competitions,
    'Showcase/Expo': CATEGORY_COLORS.competitions,

    // Funding
    'Grant Opportunity': CATEGORY_COLORS.funding,
    'Funding Partnership': CATEGORY_COLORS.funding,
    Scholarship: CATEGORY_COLORS.funding,
    Fellowship: CATEGORY_COLORS.funding,
    Sponsorship: CATEGORY_COLORS.funding,

    // Community
    Volunteer: CATEGORY_COLORS.community,
    'Community Project': CATEGORY_COLORS.community,
    'Service Learning': CATEGORY_COLORS.community,
};

const DEFAULT_TYPE_COLOR = '#546E7A';

// Category colors for legend displays
const TYPE_CATEGORY_COLORS = [
    { label: 'Work-Based Learning', color: CATEGORY_COLORS.workBasedLearning },
    { label: 'Mentoring', color: CATEGORY_COLORS.mentoring },
    { label: 'Programs & Camps', color: CATEGORY_COLORS.programsCamps },
    { label: 'Workshops', color: CATEGORY_COLORS.workshops },
    { label: 'Events', color: CATEGORY_COLORS.events },
    { label: 'Competitions', color: CATEGORY_COLORS.competitions },
    { label: 'Funding', color: CATEGORY_COLORS.funding },
    { label: 'Community', color: CATEGORY_COLORS.community },
];

/**
 * Get the hex color for an opportunity type
 * @param {string} type - Opportunity type name
 * @returns {string} Hex color code
 */
export function getTypeColor(type) {
    return OPPORTUNITY_TYPE_COLORS[type] || DEFAULT_TYPE_COLOR;
}

export { OPPORTUNITY_TYPE_COLORS, DEFAULT_TYPE_COLOR, TYPE_CATEGORY_COLORS };
